class H1FeatureTabs extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.items = Array.from(this.querySelectorAll('[data-feature-item]'));
    this.images = Array.from(this.querySelectorAll('[data-feature-image]'));
    this.panels = Array.from(this.querySelectorAll('[data-feature-panel]'));
    this.contents = Array.from(this.querySelectorAll('[data-feature-item-content]'));
    this.copies = Array.from(this.querySelectorAll('.h1-feature-tabs__copy'));
    this.mobileMedia = Array.from(this.querySelectorAll('.h1-feature-tabs__mobile-media'));
    this.triggers = Array.from(this.querySelectorAll('[data-feature-trigger]'));
    this.list = this.querySelector('.h1-feature-tabs__list');
    this.root = this.closest('[data-h1-feature-scroll]');
    this.track = this.root?.querySelector('[data-h1-feature-track]');
    this.sticky = this.root?.querySelector('.h1-feature-scroll__sticky');
    this.stage = this.root?.querySelector('[data-h1-feature-stage]');
    this.mediaQuery = window.matchMedia('(min-width: 1024px)');
    this.expandedMobileQuery = window.matchMedia('(max-width: 767px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeIndex = 0;
    this.frame = 0;
    this.start = 0;
    this.distance = 1;

    if (
      !this.items.length ||
      !this.list ||
      !this.root ||
      !this.track ||
      !this.sticky ||
      !this.stage
    ) {
      return;
    }

    this.onScrollBound = () => this.requestUpdate();
    this.onResizeBound = () => this.measure();
    this.onModeChangeBound = () => this.measure();
    this.onKeydownBound = (event) => {
      if (this.isExpandedMobile) return;

      const trigger = event.target.closest('[data-feature-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      const index = Number(trigger.dataset.featureTrigger);
      this.pendingKeyboardFocusIndex = !this.isDesktop ? index : null;
      this.scrollToFeature(index);
    };
    this.onClickBound = (event) => {
      if (this.isExpandedMobile) return;

      const trigger = event.target.closest('[data-feature-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const index = Number(trigger.dataset.featureTrigger);
      this.pendingKeyboardFocusIndex = null;
      this.scrollToFeature(index);
    };
    this.onBlockSelectBound = (event) => {
      const item = event.target.closest('[data-feature-item]');
      if (!item || !this.contains(item)) return;

      if (this.isExpandedMobile) {
        item.scrollIntoView({ behavior: 'auto', block: 'center' });
        return;
      }

      const index = Number(item.dataset.featureItem);
      this.pendingKeyboardFocusIndex = null;
      this.scrollToFeature(index, 'auto');
    };

    this.setupImagePreloading();
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this.stage);
    this.copies.forEach((copy) => this.resizeObserver.observe(copy));
    this.mediaQuery.addEventListener?.('change', this.onModeChangeBound);
    this.expandedMobileQuery.addEventListener?.('change', this.onModeChangeBound);
    this.reducedMotion.addEventListener?.('change', this.onModeChangeBound);
    this.addEventListener('keydown', this.onKeydownBound);
    this.addEventListener('click', this.onClickBound);
    this.addEventListener('shopify:block:select', this.onBlockSelectBound);
    window.addEventListener('scroll', this.onScrollBound, { passive: true });
    window.addEventListener('resize', this.onResizeBound);

    this.isInitialized = true;
    this.measure();
  }

  setupImagePreloading() {
    const preload = () => {
      const selector = this.isDesktop
        ? '.h1-feature-tabs__desktop-media .h1-feature-tabs__image'
        : '.h1-feature-tabs__mobile-media .h1-feature-tabs__image';
      const images = Array.from(this.querySelectorAll(selector));

      images.forEach((image, index) => {
        preloadResponsiveImage(image, index === this.activeIndex ? 'high' : 'low');
      });
    };

    if (!('IntersectionObserver' in window)) {
      preload();
      return;
    }

    this.imagePreloadObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        preload();
        observer.disconnect();
      },
      { rootMargin: '600px 0px' },
    );
    this.imagePreloadObserver.observe(this);
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.frame);
    this.imagePreloadObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.mediaQuery?.removeEventListener?.('change', this.onModeChangeBound);
    this.expandedMobileQuery?.removeEventListener?.('change', this.onModeChangeBound);
    this.reducedMotion?.removeEventListener?.('change', this.onModeChangeBound);
    this.removeEventListener('keydown', this.onKeydownBound);
    this.removeEventListener('click', this.onClickBound);
    this.removeEventListener('shopify:block:select', this.onBlockSelectBound);
    window.removeEventListener('scroll', this.onScrollBound);
    window.removeEventListener('resize', this.onResizeBound);
    this.track?.style.removeProperty('height');
    this.root?.classList.remove('is-enhanced');
    this.classList.remove('is-mobile-bottom-aligned', 'is-mobile-expanded');
    this.style.removeProperty('--h1-feature-mobile-media-size');
    this.isInitialized = false;
  }

  get isDesktop() {
    return this.mediaQuery.matches;
  }

  get isExpandedMobile() {
    return this.expandedMobileQuery.matches;
  }

  scrollToFeature(index, behavior) {
    if (this.isExpandedMobile) return;
    if (!Number.isInteger(index) || index < 0 || index >= this.items.length) return;

    const stableLocalProgress = this.isDesktop ? 0.95 : 0.25;
    const progress = (index + stableLocalProgress) / this.items.length;
    const top = this.start + clamp(progress, 0, 1) * this.distance;

    window.scrollTo({
      top,
      behavior: behavior || (this.reducedMotion.matches ? 'auto' : 'smooth'),
    });
  }

  measure() {
    if (!this.isConnected || !this.stage) return;

    if (this.isExpandedMobile) {
      this.setExpandedMobileState();
      return;
    }

    this.classList.remove('is-mobile-expanded');
    this.root.classList.add('is-enhanced');

    const stageHeight = Math.max(this.stage.getBoundingClientRect().height, 1);
    const scrollDistance = Math.max(window.innerHeight * 0.9 * this.items.length, 1);
    const stickyTop = Number.parseFloat(window.getComputedStyle(this.sticky).top) || 0;

    this.track.style.height = `${Math.ceil(stageHeight + scrollDistance)}px`;
    this.start = window.scrollY + this.track.getBoundingClientRect().top - stickyTop;
    this.distance = Math.max(this.track.offsetHeight - stageHeight, 1);

    if (!this.isDesktop) {
      const collapsedRowsHeight = Math.max(this.items.length - 1, 0) * 56;
      const borderHeight = this.items.length + 1;
      const mediaTopSpacing = 16;
      const flexSafetySpace = 16;
      const tallestCopyHeight = Math.max(
        ...this.copies.map((copy) => copy.scrollHeight),
        0,
      );
      const availableMediaHeight =
        this.list.clientHeight -
        collapsedRowsHeight -
        borderHeight -
        tallestCopyHeight -
        mediaTopSpacing -
        flexSafetySpace;
      const mediaSize = Math.max(
        Math.min(Math.floor(availableMediaHeight), this.list.clientWidth),
        0,
      );

      this.style.setProperty('--h1-feature-mobile-media-size', `${Math.round(mediaSize)}px`);
    } else {
      this.style.removeProperty('--h1-feature-mobile-media-size');
      this.classList.remove('is-mobile-bottom-aligned');
    }

    this.panelHeights = this.panels.map((panel, index) => {
      if (this.isDesktop) return panel.scrollHeight;

      const mobileMedia = this.mobileMedia[index];
      const mediaHeight = mobileMedia?.offsetHeight || 0;
      const mediaMarginTop = mobileMedia
        ? Number.parseFloat(window.getComputedStyle(mobileMedia).marginTop) || 0
        : 0;
      const copyHeight = this.copies[index]?.scrollHeight || 0;
      return mediaMarginTop + mediaHeight + copyHeight;
    });

    if (!this.isDesktop) {
      const collapsedRowsHeight = Math.max(this.items.length - 1, 0) * 56;
      const borderHeight = this.items.length + 1;
      const tallestPanelHeight = Math.max(...this.panelHeights, 0);
      const requiredHeight = collapsedRowsHeight + borderHeight + tallestPanelHeight;
      const availableFlexSpace = this.list.clientHeight - requiredHeight;

      this.classList.toggle('is-mobile-bottom-aligned', availableFlexSpace >= 16);
    }

    this.requestUpdate();
  }

  setExpandedMobileState() {
    window.cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.track.style.removeProperty('height');
    this.root.classList.remove('is-enhanced');
    this.classList.add('is-mobile-expanded');
    this.classList.remove('is-mobile-bottom-aligned');
    this.style.removeProperty('--h1-feature-mobile-media-size');

    this.items.forEach((item, index) => {
      item.classList.remove('is-active');
      item.style.removeProperty('--h1-feature-open');
      item.style.removeProperty('--h1-feature-collapsed-title-opacity');
      item.style.removeProperty('--h1-feature-panel-title-opacity');
      item.style.removeProperty('--h1-feature-panel-current-height');
      item.style.removeProperty('--h1-feature-title-height');
      item.style.removeProperty('--h1-feature-title-padding');
      item.style.removeProperty('--h1-feature-active-offset');
      this.triggers[index]?.setAttribute('aria-expanded', 'true');
      if (this.triggers[index]) this.triggers[index].tabIndex = -1;
      this.panels[index]?.setAttribute('aria-hidden', 'false');
    });
  }

  update() {
    this.frame = 0;
    if (this.isExpandedMobile) return;

    const progress = clamp((window.scrollY - this.start) / this.distance, 0, 1);
    const scaledProgress = Math.min(progress * this.items.length, this.items.length - 0.0001);
    const index = progress >= 1 ? this.items.length - 1 : Math.floor(scaledProgress);
    const rawLocalProgress = progress >= 1 ? 1 : scaledProgress - index;
    const localProgress = this.reducedMotion.matches
      ? rawLocalProgress >= 0.5
        ? 1
        : 0
      : smoothStep(rawLocalProgress);

    if (this.isDesktop) {
      this.setDesktopState(index, localProgress);
    } else {
      this.setMobileState(index, rawLocalProgress);
    }
  }

  setDesktopState(index, localProgress) {
    this.setActiveIndex(index);
    this.triggers.forEach((trigger) => {
      trigger.tabIndex = 0;
    });
    this.style.setProperty('--h1-feature-local-progress', localProgress.toFixed(4));

    const item = this.items[index];
    const content = this.contents[index];
    if (!item || !content) return;

    const shift = Math.max(item.clientHeight - content.offsetHeight, 0);
    item.style.setProperty(
      '--h1-feature-active-offset',
      `${Math.round(shift * (1 - localProgress))}px`,
    );
  }

  setMobileState(index, rawLocalProgress) {
    const lastIndex = this.items.length - 1;
    let transitionProgress = 0;

    if (index < lastIndex && !this.reducedMotion.matches) {
      transitionProgress = smoothStep(clamp((rawLocalProgress - 0.55) / 0.45, 0, 1));
    }

    const dominantIndex = transitionProgress >= 0.7 ? Math.min(index + 1, lastIndex) : index;
    this.setActiveIndex(dominantIndex);

    this.items.forEach((item, itemIndex) => {
      let openProgress = 0;
      if (itemIndex === index) openProgress = 1 - transitionProgress;
      if (itemIndex === index + 1) openProgress = transitionProgress;
      if (index === lastIndex && itemIndex === lastIndex) openProgress = 1;

      const collapsedTitleOpacity = 1 - clamp(openProgress / 0.35, 0, 1);
      const panelTitleOpacity = clamp((openProgress - 0.7) / 0.3, 0, 1);

      if (this.triggers[itemIndex]) {
        this.triggers[itemIndex].tabIndex = collapsedTitleOpacity > 0.01 ? 0 : -1;
      }

      item.style.setProperty('--h1-feature-open', openProgress.toFixed(4));
      item.style.setProperty(
        '--h1-feature-collapsed-title-opacity',
        collapsedTitleOpacity.toFixed(4),
      );
      item.style.setProperty('--h1-feature-panel-title-opacity', panelTitleOpacity.toFixed(4));
      item.style.setProperty(
        '--h1-feature-panel-current-height',
        `${Math.round((this.panelHeights?.[itemIndex] || 0) * openProgress)}px`,
      );
      item.style.setProperty(
        '--h1-feature-title-height',
        `${Math.round(56 * (1 - openProgress))}px`,
      );
      item.style.setProperty(
        '--h1-feature-title-padding',
        `${Math.round(4 * (1 - openProgress))}px`,
      );
    });
  }

  setActiveIndex(index) {
    if (index < 0 || index >= this.items.length) return;
    this.activeIndex = index;

    this.items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);
      this.triggers[itemIndex]?.setAttribute('aria-expanded', String(isActive));
      this.panels[itemIndex]?.setAttribute('aria-hidden', String(!isActive));
    });

    this.images.forEach((image, imageIndex) => {
      image.classList.toggle('is-active', imageIndex === index);
    });

    const activeImage = this.isDesktop
      ? this.images[index]?.querySelector('img')
      : this.panels[index]?.querySelector('.h1-feature-tabs__mobile-media img');
    preloadResponsiveImage(activeImage, 'high');

    if (!this.isDesktop && this.pendingKeyboardFocusIndex === index) {
      this.panels[index]?.focus({ preventScroll: true });
      this.pendingKeyboardFocusIndex = null;
    }
  }

  requestUpdate() {
    if (!this.frame) this.frame = window.requestAnimationFrame(() => this.update());
  }
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function smoothStep(value) {
  const progress = clamp(value, 0, 1);
  return progress * progress * (3 - 2 * progress);
}

function preloadResponsiveImage(image, priority = 'low') {
  if (!image) return;

  if (image.dataset.h1Preloaded === 'true') {
    if (priority === 'high') image.fetchPriority = 'high';
    return;
  }

  image.dataset.h1Preloaded = 'true';
  image.loading = 'eager';
  image.fetchPriority = priority;

  if (image.dataset.srcset) {
    image.srcset = image.dataset.srcset;

    if (image.dataset.sizes && image.dataset.sizes !== 'auto') {
      image.sizes = image.dataset.sizes;
    } else {
      const renderedWidth = Math.ceil(image.getBoundingClientRect().width);
      if (renderedWidth > 0) image.sizes = `${renderedWidth}px`;
    }
  }

  if (image.dataset.src) image.src = image.dataset.src;

  image.classList.remove('lazyload');
}

function setupColorwaysImagePreloading(scope = document) {
  const sectionSelector = "[id$='__section_explore-the-colorways_pc']";
  const section = scope.matches?.(sectionSelector)
    ? scope
    : scope.querySelector?.(sectionSelector);
  if (!section || section.dataset.h1ImagePreloadReady === 'true') return;

  section.dataset.h1ImagePreloadReady = 'true';

  const preload = () => {
    const imageSelector = window.matchMedia('(min-width: 768px)').matches
      ? '.media__image--desktop'
      : '.media__image--mobile';

    section.querySelectorAll(imageSelector).forEach(preloadResponsiveImage);
  };

  if (!('IntersectionObserver' in window)) {
    preload();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      preload();
      observer.disconnect();
    },
    { rootMargin: '600px 0px' },
  );
  observer.observe(section);
}

if (!customElements.get('h1-feature-tabs')) {
  customElements.define('h1-feature-tabs', H1FeatureTabs);
}

setupColorwaysImagePreloading();

document.addEventListener('shopify:section:load', (event) => {
  setupColorwaysImagePreloading(event.target);
});
